import React, { useEffect, useState, useRef } from "react";
import { useThemeMode } from "../../../../_metronic/partials";
import { INSTALLATION_JOB_STORAGE_KEY } from "../InstallationJob";
import { generateMultiSignedUrls } from "../../../models/_publicCall";
import { FOLDER_NAME } from "../../../../_metronic/helpers/enums";
import toast from "react-hot-toast";

interface FileUploadProps {
  values: any;
  setFieldValue: (field: string, value: any) => void;
  fieldName: string;
  label: string;
  jobStatus: string;
  installationJobDB: any;
}

const VideoUpload: React.FC<FileUploadProps> = ({
  values,
  setFieldValue,
  fieldName,
  label,
  jobStatus,
  installationJobDB
}) => {
  const { mode } = useThemeMode();
  const [files, setFiles] = useState<{ type: string; content: string; name: string }[]>([]);
  const [showCapture, setShowCapture] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("environment");
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timer | null>(null);
  const fieldNameToDBKey: Record<string, string> = {
    video: "storeWalkthroughVideo",
  };

  // Supported video extensions
  const SUPPORTED_VIDEO_EXTENSIONS = ['.mp4', '.mkv', '.mov'];
  const SUPPORTED_VIDEO_MIME_TYPES = [
    'video/mp4',
    'video/x-matroska',
    'video/quicktime',
    'video/x-m4v',
  ];

  // Helper to check if file is a supported video
  const isVideoFile = (file: File): boolean => {
    const fileName = file.name.toLowerCase();
    const hasValidExtension = SUPPORTED_VIDEO_EXTENSIONS.some(ext => 
      fileName.endsWith(ext)
    );
    const hasValidMimeType = SUPPORTED_VIDEO_MIME_TYPES.some(type => 
      file.type.startsWith(type) || file.type === type
    );
    
    // Accept if either extension or MIME type matches (for better compatibility)
    return hasValidExtension || hasValidMimeType;
  };

  // Upload helpers
  const requestSignedUrl = async (name: string, type: string) => {
    const items = await generateMultiSignedUrls([
      {
        file_name: name,
        file_type: type,
        folder_name: (FOLDER_NAME as any).INSTALLATION_JOB_MEDIA || "installation-job-media",
      },
    ]);
    return items?.[0]?.url || "";
  };

  const uploadToSignedUrl = async (signedUrl: string, fileOrBlob: Blob, type: string) => {
    const res = await fetch(signedUrl, {
      method: "PUT",
      headers: { "Content-Type": type },
      body: fileOrBlob,
    });
    if (!res.ok) throw new Error("Upload failed");
    // Return clean URL without query params for playback/storage
    return signedUrl.split("?")[0];
  };

  // Restore from localStorage or installationJobDB
  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}");
    let savedFiles = storedData.locationNotes?.[fieldName] || [];

    // Only prefill from DB if localStorage is empty
    if (
      jobStatus !== "Completed" &&
      installationJobDB &&
      (!savedFiles || savedFiles.length === 0)
    ) {
      const dbKey = fieldNameToDBKey[fieldName];
      const dbFiles = installationJobDB.uploadImagesOrVideos?.[dbKey] || [];
      if (Array.isArray(dbFiles) && dbFiles.length > 0) {
        savedFiles = dbFiles;
      }
    }
    setFiles(savedFiles);
    // Sync to Formik + localStorage
    setFieldValue(fieldName, savedFiles);
    try {
      localStorage.setItem(
        INSTALLATION_JOB_STORAGE_KEY,
        JSON.stringify({
          ...storedData,
          locationNotes: {
            ...storedData.locationNotes,
            [fieldName]: savedFiles,
          },
        })
      );
    } catch (error) {
      console.error("Failed to save to localStorage (quota exceeded):", error);
    }
  }, [fieldName, installationJobDB, jobStatus, setFieldValue]);

  // Initialize Formik field
  useEffect(() => {
    if (!Array.isArray(values[fieldName])) setFieldValue(fieldName, []);
  }, [values, fieldName, setFieldValue]);

  // Sync to Formik + localStorage
  useEffect(() => {
    setFieldValue(fieldName, files);
    try {
      const storedData = JSON.parse(localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}");
      const updatedData = {
        ...storedData,
        locationNotes: { ...storedData.locationNotes, [fieldName]: files },
      };
      localStorage.setItem(INSTALLATION_JOB_STORAGE_KEY, JSON.stringify(updatedData));
    } catch (error) {
      console.error("Failed to save to localStorage (quota exceeded):", error);
      toast.error("Storage quota exceeded. Please reduce the number of videos or clear some data.");
    }
  }, [files, fieldName, setFieldValue]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const fileList = input.files;
    if (!fileList) return;
    
    const videoFiles = Array.from(fileList).filter(file => isVideoFile(file));
    
    if (videoFiles.length === 0) {
      toast.error("Please select video files only (.mp4, .mkv, .mov).");
      input.value = "";
      return;
    }

    setIsUploading(true);
    
    try {
      const existingNames = new Set(files.map((f) => f.name));
      const uniqueFiles: File[] = [];
      const skippedDuplicateNames: string[] = [];
      
      for (const file of videoFiles) {
        if (existingNames.has(file.name) || uniqueFiles.some((f) => f.name === file.name)) {
          skippedDuplicateNames.push(file.name);
          continue;
        }
        uniqueFiles.push(file);
        existingNames.add(file.name);
      }

      if (uniqueFiles.length === 0) {
        if (skippedDuplicateNames.length > 0) {
          toast.error(`Skipped duplicate video(s): ${Array.from(new Set(skippedDuplicateNames)).join(", ")}`);
        }
        input.value = "";
        return;
      }

      // Get all signed URLs in a single API call only for unique files
      const signedUrlRequests = uniqueFiles.map(file => ({
        file_name: file.name,
        file_type: file.type || 'video/mp4', // Fallback to mp4 if MIME type is missing
        folder_name: (FOLDER_NAME as any).INSTALLATION_JOB_MEDIA || "installation-job-media",
      }));
      
      const signedUrls = await generateMultiSignedUrls(signedUrlRequests);
      
      // Upload each unique file to its corresponding signed URL
      for (let i = 0; i < uniqueFiles.length; i++) {
        const file = uniqueFiles[i];
        const signedUrl = signedUrls?.[i]?.url;
        
        if (!signedUrl) {
          console.error(`No signed URL returned for ${file.name}`);
          toast.error(`Failed to get upload URL for ${file.name}. Please try again.`);
          continue;
        }
        
        try {
          const publicUrl = await uploadToSignedUrl(signedUrl, file, file.type || 'video/mp4');
          setFiles((prev) => [
            ...prev,
            { type: file.type || 'video/mp4', content: publicUrl, name: file.name },
          ]);
        } catch (e) {
          console.error(`VideoUpload: failed to upload ${file.name}`, e);
          toast.error(`Failed to upload ${file.name}. Please try again.`);
        }
      }

      if (skippedDuplicateNames.length > 0) {
        toast.error(`Skipped duplicate video(s): ${Array.from(new Set(skippedDuplicateNames)).join(", ")}`);
      }
    } finally {
      setIsUploading(false);
      input.value = "";
    }
  };

  const startRecording = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: true,
      });
      streamRef.current = mediaStream;
      if (videoRef.current) videoRef.current.srcObject = mediaStream;

      chunksRef.current = [];
      const mediaRecorder = new MediaRecorder(mediaStream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "video/mp4" });
        const fileName = `video_${Date.now()}.mp4`;
        try {
          setIsUploading(true);
          const signedUrl = await requestSignedUrl(fileName, blob.type);
          if (!signedUrl) throw new Error("No signed URL returned");
          const publicUrl = await uploadToSignedUrl(signedUrl, blob, blob.type);
          setFiles((prev) => [...prev, { type: blob.type, content: publicUrl, name: fileName }]);
        } catch (e) {
          console.error("VideoUpload: failed to upload recorded video", e);
          toast.error("Failed to upload recorded video. Please try again.");
        } finally {
          setIsUploading(false);
        }

        mediaStream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        setRecording(false);
        stopTimer();
        setElapsedTime(0);
      };

      mediaRecorder.start();
      setRecording(true);
      startTimer();
    } catch (err) {
      console.error(err);
      setCameraError("Cannot access camera. Back camera may not be available.");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setShowCapture(false);
  };

  const toggleCamera = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      stopTimer();
      setElapsedTime(0);
      setRecording(false);
    }
  };

  const handleRemoveFile = (fileContent: string) => {
    setFiles((prev) => {
      const newFiles = prev.filter((file) => file.content !== fileContent);
      setFieldValue(fieldName, newFiles);
      return newFiles;
    });
  };

  const startTimer = () => {
    stopTimer();
    timerRef.current = setInterval(() => setElapsedTime((prev) => prev + 1), 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="wo-form-section mt-8">
      <div className="upload-section">
        <h3>{label}</h3>
        <div className={`upload-box ${mode === "dark" ? "dark-upload-box" : ""}`}>
          <input 
            type="file" 
            className="file-input" 
            multiple 
            accept=".mp4,.mkv,.mov,video/mp4,video/x-matroska,video/quicktime" 
            onChange={handleFileChange} 
          />

          {files.length > 0 ? (
            <div className="preview-container d-flex flex-wrap mt-3">
              {files.map((file, idx) => (
                <div key={idx} className="video-box mt-3 ms-2 justify-content-center">
                  <video
                    controls
                    src={file.content}
                    width="200"
                    className="img-wrapper overflow-hidden m-2 ms-0 d-flex position-relative"
                  />
                  <button type="button" onClick={() => handleRemoveFile(file.content)} className="close-btn img-btn">
                    <i className="fa fa-close fs-5 p-1"></i>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="file-label text-center p-3">
              <i className="fa fa-cloud-upload cloud-icon fs-3"></i>
              <span className="d-block mt-2">Browse Videos</span>
              <p className="text-muted">Drag and drop video files here (.mp4, .mkv, .mov)</p>
            </div>
          )}
          {isUploading && (
            <div className="d-flex align-items-center mt-2">
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              <span>Uploading...</span>
            </div>
          )}
        </div>

        <div className="mt-3">
          {!showCapture && (
            <button type="button" className="btn btn-primary" onClick={() => setShowCapture(true)}>
              Capture Video
            </button>
          )}

          {showCapture && (
            <div className="mt-3 text-center">
              {cameraError && <div className="alert alert-danger mt-2">{cameraError}</div>}

              {/* Small preview */}
              <video
                ref={videoRef}
                autoPlay
                muted
                width="300"
                className="video-preview-box border rounded"
              ></video>

              {recording && <div className="mt-2 fw-bold fs-5">Recording: {formatTime(elapsedTime)}</div>}

              <div className="mt-3 d-flex justify-content-center gap-3">
                <button type="button" className="btn btn-secondary" onClick={toggleCamera}>
                  Switch to {facingMode === "user" ? "Back" : "Front"} Camera
                </button>

                {recording ? (
                  <button type="button" className="btn btn-danger" onClick={stopRecording}>
                    Stop Recording
                  </button>
                ) : (
                  <button type="button" className="btn btn-primary" onClick={startRecording}>
                    Start Recording
                  </button>
                )}

                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => {
                    setShowCapture(false);
                    setCameraError(null);
                    if (streamRef.current) streamRef.current.getTracks().forEach((track) => track.stop());
                    stopTimer();
                    setElapsedTime(0);
                    setRecording(false);
                  }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoUpload;